import { FC } from 'react';
import {
  AutocompleteInput,
  ChipField,
  CreateButton,
  Datagrid,
  FilterButton,
  List,
  ListProps,
  ReferenceField,
  ReferenceInput,
  SearchInput,
  SelectInput,
  TextField,
  TopToolbar,
  useCreatePath,
} from 'react-admin';
import { useNavigate } from 'react-router-dom';
import { useWatch } from 'react-hook-form';
import HardwareIcon from '../hardware/icons/HardwareIcon';
import BatchAssignBulkAction from './components/BatchAssignBulkAction';
import DownloadReportsBulkAction from './components/DownloadReportsBulkAction';
import ExportUnitsCsvButton from './components/ExportUnitsCsvButton';
import RevokeKeysBulkAction from './components/RevokeKeysBulkAction';
import RelativeTimeField from './RelativeTimeField';
import { SimCardField } from './SimCardField';
import SshKeyBadge from './components/SshKeyBadge';

const UpdateChannelFilter = ({ source, reference }: { source: string; reference: string }) => {
  const hardwareId = useWatch({ name: 'hardware_id' });
  return (
    <ReferenceInput
      source={source}
      reference={reference}
      filter={hardwareId ? { hardware_id: hardwareId } : {}}
      disabled={!hardwareId}
      alwaysOn
    >
      <SelectInput source="name" optionText={(option) => option.name} emptyText="ra.action.select_all" />
    </ReferenceInput>
  );
};

const UnitListFilters = [
  <SearchInput source="q" alwaysOn />,
  <ReferenceInput source="designation_id" reference="designations">
    <SelectInput source="name" optionText={(option) => option.name} emptyText="ra.action.select_all" />
  </ReferenceInput>,
  <ReferenceInput source="locale_id" reference="locales">
    <SelectInput source="name" optionText={(option) => option.name} emptyText="ra.action.select_all" />
  </ReferenceInput>,
  <ReferenceInput source="partner_id" reference="partners">
    <AutocompleteInput source="name" optionText={(option) => option.name} />
  </ReferenceInput>,
  <ReferenceInput source="country_id" reference="countries">
    <AutocompleteInput source="name" optionText={(option) => option.name} />
  </ReferenceInput>,
  <ReferenceInput source="collections" reference="collections">
    <AutocompleteInput source="name" optionText={(option) => option.name} />
  </ReferenceInput>,
  <ReferenceInput source="hardware_id" reference="hardware">
    <SelectInput source="name" optionText={(option) => option.name} emptyText="ra.action.select_all" />
  </ReferenceInput>,
  <UpdateChannelFilter source="update_channel_id" reference="update_channels" />,
  <SelectInput
    source="online_status"
    choices={[
      { name: 'Online', id: 'online' },
      { name: 'Realtime', id: 'realtime' },
      { name: 'Offline', id: 'offline' },
    ]}
  />,
  <SelectInput
    source="hasSimCard"
    choices={[
      { name: 'Has sim card', id: true },
      { name: "Doesn't have sim card", id: false },
    ]}
  />,
  <SelectInput
    source="ssh_key_state"
    choices={[
      { name: 'Valid', id: 'valid' },
      { name: 'Expired', id: 'expired' },
      { name: 'Revoked', id: 'revoked' },
      { name: 'No Key', id: 'empty' },
    ]}
  />,
];

const UnitListActions = () => (
  <TopToolbar>
    <FilterButton />
    <CreateButton />
    <ExportUnitsCsvButton />
  </TopToolbar>
);

const UnitList: FC<ListProps> = () => {
  const createPath = useCreatePath();
  const navigate = useNavigate();

  const handleRowClick = (id: any): false => {
    navigate(createPath({ resource: 'units', type: 'show', id }) + '/info');
    return false;
  };

  return (
    <List
      perPage={25}
      filters={UnitListFilters}
      exporter={false}
      sort={{ field: 'serial', order: 'ASC' }}
      actions={<UnitListActions />}
    >
      <Datagrid
        rowClick={handleRowClick}
        bulkActionButtons={
          <>
            <RevokeKeysBulkAction />
            <DownloadReportsBulkAction />
            <BatchAssignBulkAction />
          </>
        }
        sx={{
          '& .column-sim_id': { width: '3em' },
          '& .column-online_status': { width: '3em' },
          '& .column-ssh_key_state': { width: '8em' },
        }}
      >
        <TextField source="serial" />
        <TextField source="hostname" emptyText="-" />
        <ReferenceField reference="hardware" source="hardware_id" emptyText="-" link={false}>
          <HardwareIcon source="name" />
        </ReferenceField>
        <ReferenceField source="partner_id" reference="partners" emptyText="-" link="show">
          <ChipField source="name" />
        </ReferenceField>
        <ReferenceField source="designation_id" reference="designations" emptyText="-" link="show">
          <ChipField source="name" />
        </ReferenceField>
        <ReferenceField source="update_channel_id" reference="update_channels">
          <TextField source="name" />
        </ReferenceField>
        <SshKeyBadge source="ssh_key_state" />
        <RelativeTimeField source="last_online" />
        <SimCardField source="sim_id" />
      </Datagrid>
    </List>
  );
};

export default UnitList;
